import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import withTranslations from '../../hocs/withTranslations';
import { ITranslations } from '../../interfaces/ITranslationFields';
import CartItem from '../CartItems';
import { CartItemSkeleton } from '../CartItems/CartItemSkeleton';
import GiftCardWrapItem from '../CartItems/GiftCardWrapItem';
import CartItemEnitity, { SKU_TYPE } from '../../domain/entities/Cart/CartItem';
import { IGetPriceOptions } from '../../controllers/OrderSummaryViewController/OrderSummaryViewController';
import DateUtils from '../../utils/DateUtils';
import Constants from '../../constants/Constants';

const ShoppingBagCartItems = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
`;

const ShipmentHeader = styled.p`
  font-family: var(--font_regular_bold);
  font-size: 13px;
  margin-top: 10px;
  margin-bottom: 8px;
`;

const ArrivalTextLabel = styled.p`
  font-size: 13px;
  color: var(--color_black);
  margin-bottom: 6px;
`;

const ShipmentBox = styled.div`
  box-shadow: 1px 1px 3px 2px var(--color_light_gray_border);
  display: flex;
  flex-direction: column;
  gap: 20px;

  .acac-cart-item-total-price {
    padding-right: 5px;
  };
`;

interface IExpandedShoppingBagProps {
  enableSplits: boolean;
  locale: string;
  defaultShippingDays?: string;
  baseUrl: string;
  isBopisOrCourier?: boolean;
  cartItems: CartItemEnitity[];
  getPrice(price: number, options?: IGetPriceOptions): string;
  itemNotAvailableForPickUp: boolean;
  translations?: ITranslations;
  showFreeLabelAsPrice?: boolean;
  shipGroupedCartItems?: Map<string, CartItemEnitity[]>;
}

@observer
class ExpandedShoppingBag extends React.Component<IExpandedShoppingBagProps> {

  private returnCartItemElements(cartItems: CartItemEnitity[], enableSplits = false): JSX.Element {
    const {
      baseUrl,
      getPrice,
      itemNotAvailableForPickUp,
      showFreeLabelAsPrice,
      isBopisOrCourier,
    } = this.props;

    return (
      <>
        {
          cartItems.map(item => {
            switch (item.skuType) {
              case SKU_TYPE.GiftWrap:
              case SKU_TYPE.GiftCard:
                return (
                  <GiftCardWrapItem
                    baseUrl={baseUrl}
                    item={item}
                    getPrice={getPrice}
                  />
                );
              default:
                return (
                  <CartItem
                    key={item.skuBaseId}
                    baseUrl={baseUrl}
                    item={item}
                    isBopisOrCourier={isBopisOrCourier}
                    getPrice={getPrice}
                    itemNotAvailableForPickUp={itemNotAvailableForPickUp}
                    showFreeLabelAsPrice={showFreeLabelAsPrice}
                    splitsEnabled={enableSplits}
                  />
                );
            }
          })
        }
      </>
    );
  }

  private renderShoppingCartItemsSplit(): JSX.Element {
    const {
      enableSplits,
      locale,
      cartItems,
      defaultShippingDays,
      shipGroupedCartItems,
      translations: {
        arrivesBy,
        orderShipment,
      },
    } = this.props;

    if (enableSplits && shipGroupedCartItems) {
      const dates = Array.from(shipGroupedCartItems.keys()).sort();

      return (
        <ShoppingBagCartItems>
          {
            dates.map((date, index) => {
              const formattedDate = (date === Constants.NO_DATE_CART_ITEM) ? defaultShippingDays : arrivesBy.replace('::date::', DateUtils.getFormattedDateFromString(date, locale));
              const shipmentNumber = orderShipment + ` (${index + 1})`;

              return (
                <React.Fragment key={date}>
                  <ShipmentHeader>{shipmentNumber}</ShipmentHeader>
                  <ArrivalTextLabel>{formattedDate}</ArrivalTextLabel>
                  <ShipmentBox>
                    {
                      this.returnCartItemElements(shipGroupedCartItems.get(date))
                    }
                  </ShipmentBox>
                </React.Fragment>
              );
            })
          }
        </ShoppingBagCartItems>
      );
    }

    return (
      <ShoppingBagCartItems>
        {
          this.returnCartItemElements(cartItems)
        }
      </ShoppingBagCartItems>);
  }

  render(): JSX.Element {
    const {
      cartItems,
    } = this.props;

    if (cartItems && cartItems.length) {
      return (
        <>
          {this.renderShoppingCartItemsSplit()}
        </>
      );
    }

    return (
      <ShoppingBagCartItems>
        <CartItemSkeleton />
        <CartItemSkeleton />
        <CartItemSkeleton />
      </ShoppingBagCartItems>
    );
  }
}

export default withTranslations(ExpandedShoppingBag);
